import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Pencil, FileText, Link2 } from 'lucide-react';
import { Tab, Button, ActionDropdown, TanStackTable, DeleteModal, PublishModal, UnpublishModal, Pagination, type ColumnDef, type TabItem, type DropdownAction } from '@/components/ui';
import { usePublications, usePublicationStats, useDeletePublication, useSearchPublications, useUpdatePublicationStatus } from '@/lib/api/hooks/publication';
import { showToast } from '@/lib/toast';
import type { Publication } from '@/lib/api/types';
import PublicationsStats from './PublicationsStats';
import handleDownloadFromUrl from '@/utils/helpers/handleDownloadFromUrl';
import { Paragraph } from '@/components/ui/Typography';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { usePublicationsFilterSections } from '@/pages/resources/publications/FilterModule/FILTER_SECTIONS';

const Publications: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState<{ id: string; title: string } | null>(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedStatusUpdate, setSelectedStatusUpdate] = useState<{ id: string; title: string; status: 'DRAFT' | 'PUBLISH' } | null>(null);

  // Fetch stats
  const { data: statsData } = usePublicationStats();
  const stats = statsData || { total: 0, totalPublished: 0, totalDrafts: 0, totalViews: 0, totalDownloads: 0 };

  const FILTER_SECTIONS = usePublicationsFilterSections();
            // ── URL-driven search / filter state ──────────────────────────────────────
            const {
              mode,
              searchTerm,
              filterState,
              page,
              size,
              setSearch,
              setFilters,
              removeFilter,
              clearFilters,
              setPage,
              apiFilters,
              getActiveFilterCount
            } = useSearchFilterParams(FILTER_SECTIONS)
          
        
        
          // Search API (when searching)
          const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchPublications(
            { page, size },
          searchTerm,
            mode === 'search' 
          )
        
          // Filter API (when filtering or default)
          const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = usePublications(
            { page, size },
            {   status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT', ...apiFilters },
              mode === 'filter' || mode === 'default'
          )
        
          // Select appropriate data source: search takes priority
          const aggregateData = mode === 'search' ? searchData : filterData
          const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
          const isError = mode === 'search' ? isSearchError : isFilterError
        
          const totalPages = aggregateData?.data?.totalPage ?? 1
          const activeFilterCount = getActiveFilterCount()
          const refetchData = mode === 'search' ? refetchSearch : refetchFilter
          const emptyMessage = mode === 'search'
            ? 'No publications found matching your search criteria.'
            : mode === "filter" ?  'No publications found matching your filter criteria.'
            : 'No publications found.'
  
    const publications = aggregateData?.data?.data || [];

  const { mutate: deletePublication, isPending: isDeleting } = useDeletePublication();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdatePublicationStatus();

  const publishedCount = stats.totalPublished;
  const draftsCount = stats.totalDrafts;

  const tabItems: TabItem<'published' | 'drafts'>[] = [
    { key: 'published', label: 'Published', count: publishedCount },
    { key: 'drafts', label: 'Drafts', count: draftsCount }
  ];

  const handleViewDetails = (publicationId: string) => {
    navigate(`/admin/publications/${publicationId}`);
  };

  const handleEdit = (publicationId: string) => {
    navigate(`/admin/publications/${publicationId}/edit`);
  };

  const handleDeleteClick = (publication: Publication) => {
    setSelectedPublication({ id: publication.publicationId, title: publication.title });
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedPublication) return;
    
    deletePublication(selectedPublication.id, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Publication deleted successfully!');
          setDeleteModalOpen(false);
          setSelectedPublication(null);
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  const handleStatusUpdateClick = (publication: Publication, status: 'DRAFT' | 'PUBLISH') => {
    setSelectedStatusUpdate({ id: publication.publicationId, title: publication.title, status });
    if (status === 'PUBLISH') {
      setPublishModalOpen(true);
    } else {
      setUnpublishModalOpen(true);
    }
  };

  const confirmStatusUpdate = () => {
    if (!selectedStatusUpdate) return;
    
    updateStatus(
      { publicationId: selectedStatusUpdate.id, status: selectedStatusUpdate.status },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              selectedStatusUpdate.status === 'PUBLISH'
                ? 'Publication published successfully!'
                : 'Publication unpublished successfully!'
            );
            setPublishModalOpen(false);
            setUnpublishModalOpen(false);
            setSelectedStatusUpdate(null);
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const handleDownload = (publicationUrl: string, title: string) => {
    handleDownloadFromUrl({ url: publicationUrl, fileName: title });
  };

  const getDropdownActions = (publication: Publication): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'View Details',
        onClick: (e) => {
          e.stopPropagation();
          handleViewDetails(publication.publicationId);
        }
      },
      {
        label: 'Edit',
        onClick: (e) => {
          e.stopPropagation();
          handleEdit(publication.publicationId);
        }
      }
    ];

    if (publication.publicationUrl) {
      actions.push({
        label: 'Download',
        onClick: (e) => {
          e.stopPropagation();
          handleDownload(publication.publicationUrl!, publication.title);
        }
      });
    }

    // Add status-specific actions
    if (publication.status === 'PUBLISH') {
      actions.push({
        label: 'Unpublish',
        onClick: (e) => {
          e.stopPropagation();
          handleStatusUpdateClick(publication, 'DRAFT');
        },
        disabled: isUpdatingStatus
      });
    } else {
      actions.push({
        label: 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          handleStatusUpdateClick(publication, 'PUBLISH');
        },
        disabled: isUpdatingStatus
      });
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteClick(publication);
        },
        variant: 'danger' as const,
        disabled: isDeleting
      });
    }

    return actions;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatCategory = (category: string) => {
    return category
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getCategoryBadgeClass = (category: string) => {
    if (category.includes('LEGAL')) return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    if (category.includes('RESEARCH')) return 'border-purple-200 bg-purple-50 text-purple-700';
    if (category.includes('DESIGN')) return 'border-amber-200 bg-amber-50 text-amber-700';
    if (category.includes('ENVIRONMENT')) return 'border-teal-200 bg-teal-50 text-teal-700';
    if (category.includes('REGIONAL')) return 'border-blue-200 bg-blue-50 text-blue-700';
    if (category.includes('INTERNAL')) return 'border-gray-200 bg-gray-100 text-gray-700';
    return 'border-gray-200 bg-gray-50 text-gray-700';
  };

  const getStatusConfig = (status: 'DRAFT' | 'PUBLISH') => {
    if (status === 'PUBLISH') {
      return {
        label: 'Published',
        badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        dot: 'bg-emerald-500'
      };
    }

    return {
      label: 'Draft',
      badge: 'border-blue-200 bg-blue-50 text-blue-700',
      dot: 'bg-blue-500'
    };
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const getFileExtension = (publication: Publication) => {
    const source = publication.publicationUrl || publication.externalLink;
    if (!source) return 'FILE';

    try {
      const clean = source.split('?')[0].split('#')[0];
      const ext = clean.includes('.') ? clean.substring(clean.lastIndexOf('.') + 1).toUpperCase() : '';
      return ext || 'FILE';
    } catch {
      return 'FILE';
    }
  };

  const getFileColorClass = (ext: string) => {
    if (ext === 'PDF') return 'text-red-600';
    if (ext === 'DOC' || ext === 'DOCX') return 'text-blue-600';
    if (ext === 'PPT' || ext === 'PPTX') return 'text-orange-600';
    return 'text-gray-600';
  };

  const handleRowClick = (pub: Publication) => {
    if (activeTab === 'published') {
      navigate(`/admin/publications/${pub.publicationId}`);
    } else {
      navigate(`/admin/publications/${pub.publicationId}/edit`);
    }
  };

  const columns: ColumnDef<Publication, unknown>[] = [
    {
      id: 'publication',
      header: 'Publication',
      cell: ({ row }) => {
        const pub = row.original;
        return (
          <div className="flex items-center gap-3 min-w-[350px]">
            {pub.coverImageUrl ? (
              <img
                src={pub.coverImageUrl}
                alt={pub.title}
                className="w-12 h-12 rounded-md object-cover border border-gray-200 shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-md border border-gray-200 bg-gray-100 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 whitespace-normal break-words leading-5">{pub.title}</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-3 align-top' },
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => {
        const pub = row.original;
        const categoryLabel = formatCategory(pub.publicationCategory);
        return (
          <span className={`inline-flex items-center rounded-lg border px-2.5 py-1 text-xs font-medium ${getCategoryBadgeClass(pub.publicationCategory)}`}>
            {categoryLabel}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-3 whitespace-nowrap' },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const { label, badge, dot } = getStatusConfig(row.original.status);
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium ${badge}`}>
            <span className={`h-2 w-2 rounded-full ${dot}`} />
            {label}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-3 whitespace-nowrap' },
    },
    {
      id: 'published',
      header: 'Published',
      cell: ({ row }) => {
        const pub = row.original;
        return <span className="text-sm text-gray-700">{pub.datePublished ? formatDate(pub.datePublished) : '-'}</span>;
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-3 whitespace-nowrap' },
    },
    {
      id: 'author',
      header: 'Author',
      cell: ({ row }) => {
        const pub = row.original;
        return (
          <div className="flex items-center gap-2 max-w-[180px]">
            <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center shrink-0">
              {getInitials(pub.createdByName || 'User')}
            </div>
            <span className="text-sm text-gray-800 leading-5 whitespace-normal break-words">{pub.createdByName}</span>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-3' },
    },
    {
      id: 'views',
      header: 'Views',
      cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.totalViews ?? 0}</span>,
      meta: { headerClassName: 'px-4 py-3 text-center', cellClassName: 'px-4 py-3 text-center whitespace-nowrap' },
    },
    {
      id: 'downloads',
      header: 'Downloads',
      cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.totalDownloads ?? 0}</span>,
      meta: { headerClassName: 'px-4 py-3 text-center', cellClassName: 'px-4 py-3 text-center whitespace-nowrap' },
    },
    {
      id: 'file',
      header: 'File',
      cell: ({ row }) => {
        const pub = row.original;
        const isExternalOnly = !pub.publicationUrl && !!pub.externalLink;

        if (isExternalOnly) {
          return (
            <div className="inline-flex flex-col items-center text-blue-600">
              <Link2 className="w-5 h-5" />
              <span className="text-[10px] font-semibold mt-0.5">LINK</span>
            </div>
          );
        }

        const ext = getFileExtension(pub);
        const colorClass = getFileColorClass(ext);

        return (
          <div className={`inline-flex flex-col items-center ${colorClass}`}>
            <FileText className="w-5 h-5" />
            <span className="text-[10px] font-semibold mt-0.5">{ext}</span>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3 text-center', cellClassName: 'px-4 py-3 text-center whitespace-nowrap' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const pub = row.original;
        return (
          <div className="flex items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="Preview"
              onClick={() => handleViewDetails(pub.publicationId)}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Edit"
              onClick={() => handleEdit(pub.publicationId)}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <ActionDropdown id={pub.publicationId} actions={getDropdownActions(pub)} />
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3 text-center', cellClassName: 'px-4 py-3 whitespace-nowrap' },
    }
  ];

  const isEmptyState = !isFetching && mode === "default" && publications.length === 0;


  return (
    <>
     
      <div className="space-y-6">

        {/* Publication Stats */}
        <PublicationsStats />

        <div className="flex items-center justify-between">
          <Tab
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <Button
            variant="primary"
            onClick={() => navigate('/admin/publications/new')}
          >
            Upload New
          </Button>
        </div>

                {/* Search + Filter Controls */}
                          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                            <div className="flex items-center relative gap-4 justify-between flex-wrap">
                              <Search
                                value={searchTerm}
                                onSearch={setSearch}
                                placeholder="Search ..."
                              />
                               <FilterModule
                                    sections={FILTER_SECTIONS}
                                    activeFilters={filterState}
                                    onApply={setFilters}
                                    activeFilterCount={activeFilterCount}
                                  />
                            </div>
                
                            {/* Active filter chips — only shown in filter mode */}
                            <FilterParamsDisplay
                              sections={FILTER_SECTIONS}
                              filterState={filterState}
                              onRemove={removeFilter}
                              onClearAll={clearFilters}
                            />
                          </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm relative">  
              {isFetching ? <TanStackTable data={[]} columns={columns} loading /> :
              isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : isEmptyState ? (
                <div className="text-center py-20">
                  <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                    <img src="/No-post-icon.png" alt="No posts" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg">
                    {activeTab === 'published' ? 'No Recent Post' : 'No Draft'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {activeTab === 'published' ? 'You are yet to create a post' : 'Your draft is empty'}
                  </p>
                    <Button variant="primary" className="mt-4" onClick={() => navigate('/admin/publications/new')}>
                      Upload New
                    </Button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <TanStackTable
                      data={publications}
                      columns={columns}
                      getRowId={(row) => row.publicationId}
                      onRowClick={handleRowClick}
                      rowClassName="hover:bg-gray-50"
                    emptyMessage={emptyMessage}
                    />
                  </div>
                    <Pagination
               currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
                      className="border-t border-gray-200 p-4"
                    />
                </>
              )}
      
        </div>
      </div>

       <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedPublication(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Publication"
        subtitle="Are you sure you want to delete this publication?"
        itemName={selectedPublication?.title}
        warningText="This action cannot be undone. The publication will be permanently deleted."
        isLoading={isDeleting}
      />
      <PublishModal
        isOpen={publishModalOpen}
        onClose={() => {
          setPublishModalOpen(false);
          setSelectedStatusUpdate(null);
        }}
        onConfirm={confirmStatusUpdate}
        title="Publish Publication?"
        subtitle="Are you sure you want to publish this publication?"
        itemName={selectedStatusUpdate?.title}
        infoText="This publication will be visible to the public once published."
        itemType="Publication"
        isLoading={isUpdatingStatus}
      />
      <UnpublishModal
        isOpen={unpublishModalOpen}
        onClose={() => {
          setUnpublishModalOpen(false);
          setSelectedStatusUpdate(null);
        }}
        onConfirm={confirmStatusUpdate}
        itemType="Publication"
        isLoading={isUpdatingStatus}
      />
    </>
  );
};

export default Publications;
